import { useState, useEffect, ChangeEvent, SyntheticEvent } from 'react';
import {
  useTheme, Box, Grid, Card, Button, Divider, Typography, CardContent, useMediaQuery
} from '@mui/material';
import axios from 'axios';
import Title from '../../components/Title';
import { Office } from '../../models/office';
import { Collect } from '../../models/collect';
import { formatCurrency } from '../../utils/format/currency';
import ModalOfficeExpense from './modals/ModalOfficeExpenses';
import ModalLendRefinance from './modals/ModalLendRefinance';
import { useDrawer } from '../../components/context/DrawerContext';
import { stickyListRegionWidth } from '../../components/layouts/StickyListToolbar';
import CollectFilter from '../../components/filter/collectbyDate';
import { Search as SearchIcon } from '@mui/icons-material';
import HeaderResponsive from '../../components/layouts/HeaderResponsive';
import PageScrollArea from '../../components/layouts/PageScrollArea';
import EmptyStateMessage from '../../components/layouts/EmptyStateMessage';
import SimpleCustomTable from '../../components/table/SimpleCustomTable';
import { SimpleColumn, SimpleRow } from '../../components/table/interface';
import { nowOperationalDateString } from '../../utils/operationalDateTime';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

interface TableRow extends SimpleRow {
  id: number | string;
  collect: string;
  collect_id?: string;
  box_id?: number;
  total_pays: number;
  total_lends: number;
  total_utility: number;
  total_insurance_charge: number;
  value_refinance: number;
  total_refinance: number;
  total_off_exp_plus: number;
  total_off_exp_minus: number;
  total_expenses: number;
  currently_box: number;
  last_box: number;
  value_box: number;
}

const ResumenConsolidadoDia: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const current_date = nowOperationalDateString();

  const [office_id, setOffice_id] = useState('');
  const [collectsOffice, setCollectsOffice] = useState<Collect[]>([]);
  const [selectedCollects, setSelectedCollects] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [start_date, setStart_date] = useState(current_date);
  const [end_date, setEnd_date] = useState(current_date);
  const [datas, setDatas] = useState<any[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [openModalLend, setOpenModalLend] = useState(false);
  const [modalData, setModalData] = useState<{ boxId: number; date_start: string; date_end: string; collect: string, collect_id: string, typeOperation: number; out?: boolean } | null>(null);
  const [modalLend, setModalLend] = useState<{ boxId: number; date_start: string; date_end: string; collect: string, collect_id: string } | null>(null);
  const { drawerOpen } = useDrawer();

  useEffect(() => {
    axios.get('offices').then(({ data }) => setOffices(data.data));
  }, []);

  useEffect(() => {
    if (office_id) {
      axios.get(`collectsoffice/${office_id}`).then(({ data }) => {
        setCollectsOffice(data);
        setSelectedCollects([]);
        setSelectAll(false);
      });
    }
  }, [office_id]);

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectedCollects(prev =>
      prev.includes(value) ? prev.filter(id => id !== value) : [...prev, value]
    );
  };

  const handleSelectAll = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = collectsOffice.map(collect => collect.id.toString());
      setSelectedCollects(allIds);
      setSelectAll(true);
    } else {
      setSelectedCollects([]);
      setSelectAll(false);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalData(null);
  };

  const handleOpenModalWithType = (row: any, typeOperation: number) => {
    setModalData({
      boxId: row.box_id,
      date_start: start_date,
      date_end: end_date,
      collect_id: row.collect_id,
      collect: row.collect,
      typeOperation,
      out: typeOperation === 1,
    });
    setOpenModal(true);
  };
  const handleCloseModalLend = () => {
    setOpenModalLend(false);
    setModalLend(null);
  };
  const handleOpenModalLendType = (row: any) => {
    setModalLend({
      boxId: row.box_id,
      date_start: start_date,
      date_end: end_date,
      collect: row.collect,
      collect_id: row.collect_id,
    });
    setOpenModalLend(true);
  };
  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!selectedCollects || selectedCollects.length === 0) {
      showAlert('Debe seleccionar al menos 1 cobro', 'warning');
      return;
    }
    const formData = { end_date, start_date, collect_id: selectedCollects };
    showLoading();
    try {
      const response = await axios.post('/report/closercollectconsolidate', formData);
      setDatas(response.data.data);
      await new Promise((resolve) => setTimeout(resolve, 1000));

      if (!response.data.data || response.data.data.length === 0) {
        showAlert('No existen registros para los datos seleccionados', 'info');
        return;
      }

    } catch (error) {
      console.error('Error:', error);
      showAlert('Error al obtener datos', 'error');
    } finally {
      hideLoading();
    }
  };

  const totalRowId = 'total-row';
  const totalCellStyles = { fontWeight: 600 } as const;

  const createCurrencyCell = (
    field: keyof TableRow,
    options: { onClick?: (row: TableRow) => void } = {}
  ) => ({ row }: { row: SimpleRow }) => {
    const typedRow = row as TableRow;
    const numericValue = Number(typedRow[field] ?? 0);
    const isTotalRow = typedRow.id === totalRowId;

    const valueNode = (
      <Typography
        variant="body2"
        sx={isTotalRow ? totalCellStyles : undefined}
      >
        {formatCurrency(numericValue)}
      </Typography>
    );

    if (!options.onClick || isTotalRow) {
      return (
        <Box display="flex" alignItems="center" justifyContent="flex-end" width="100%">
          {valueNode}
        </Box>
      );
    }

    return (
      <Box display="flex" alignItems="center" justifyContent="flex-end" width="100%" gap={1}>
        {valueNode}
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={() => options.onClick?.(typedRow)}
          sx={{ minWidth: 0, padding: '2px', minHeight: 'unset', lineHeight: 1 }}
        >
          <SearchIcon fontSize="small" />
        </Button>
      </Box>
    );
  };
  const renderDataGridResumen = (resumenData: any[]) => {
    if (!resumenData || resumenData.length === 0) return null;

    // Crear filas de datos
    const dataRows: TableRow[] = resumenData.map((item, index) => ({
      id: index + 1,
      collect: item.collect_name,
      collect_id: item.collect_id,
      box_id: item.box_id,
      total_pays: item.totals.total_pays || 0,
      total_lends: item.totals.total_lends || 0,
      total_utility: item.totals.total_utility || 0,
      total_insurance_charge: item.totals.total_insurance_charge || 0,
      value_refinance: item.totals.value_refinance || 0,
      total_refinance: item.totals.total_refinance || 0,
      total_off_exp_plus: item.totals.total_off_exp_plus || 0,
      total_off_exp_minus: item.totals.total_off_exp_minus || 0,
      total_expenses: item.totals.total_expenses || 0,
      currently_box: item.totals.currently_box || 0,
      last_box: item.totals.last_box || 0,
      value_box: item.totals.value_box || 0,
    }));

    // Calcular totales
    const totals: TableRow = {
      id: totalRowId,
      collect: 'TOTAL',
      collect_id: undefined,
      box_id: undefined,
      total_pays: dataRows.reduce((sum, row) => sum + row.total_pays, 0),
      total_lends: dataRows.reduce((sum, row) => sum + row.total_lends, 0),
      total_utility: dataRows.reduce((sum, row) => sum + row.total_utility, 0),
      total_insurance_charge: dataRows.reduce((sum, row) => sum + row.total_insurance_charge, 0),
      value_refinance: dataRows.reduce((sum, row) => sum + row.value_refinance, 0),
      total_refinance: dataRows.reduce((sum, row) => sum + row.total_refinance, 0),
      total_off_exp_plus: dataRows.reduce((sum, row) => sum + row.total_off_exp_plus, 0),
      total_off_exp_minus: dataRows.reduce((sum, row) => sum + row.total_off_exp_minus, 0),
      total_expenses: dataRows.reduce((sum, row) => sum + row.total_expenses, 0),
      currently_box: dataRows.reduce((sum, row) => sum + row.currently_box, 0),
      last_box: dataRows.reduce((sum, row) => sum + row.last_box, 0),
      value_box: dataRows.reduce((sum, row) => sum + row.value_box, 0),
    };

    const rows = [...dataRows, totals];

    const columns: SimpleColumn[] = [
      {
        field: 'collect',
        headerName: 'Cobro',
        minWidth: 140,
        maxWidth: 180,
        wrapText: true,
        renderCell: ({ row }) => {
          const typedRow = row as TableRow;
          return (
            <Typography
              variant="body2"
              sx={typedRow.id === totalRowId ? totalCellStyles : undefined}
            >
              {typedRow.collect}
            </Typography>
          );
        },
      },
      {
        field: 'total_pays',
        headerName: 'Abonos',
        align: 'right',
        minWidth: 80,
        maxWidth: 100,
        renderCell: createCurrencyCell('total_pays'),
      },
      {
        field: 'total_lends',
        headerName: 'Préstamos',
        align: 'right',
        minWidth: 80,
        maxWidth: 100,
        renderCell: createCurrencyCell('total_lends'),
      },
      {
        field: 'total_off_exp_plus',
        headerName: 'Gastos Oficina +',
        align: 'right',
        minWidth: 80,
        maxWidth: 100,
        renderCell: createCurrencyCell('total_off_exp_plus', {
          onClick: (row) => handleOpenModalWithType(row, 1),
        }),
      },
      {
        field: 'total_off_exp_minus',
        headerName: 'Gastos Oficina -',
        align: 'right',
        minWidth: 80,
        maxWidth: 100,
        renderCell: createCurrencyCell('total_off_exp_minus', {
          onClick: (row) => handleOpenModalWithType(row, 2),
        }),
      },
      {
        field: 'total_expenses',
        headerName: 'Gastos Caja Cobro',
        align: 'right',
        minWidth: 80,
        maxWidth: 100,
        renderCell: createCurrencyCell('total_expenses', {
          onClick: (row) => handleOpenModalWithType(row, 3),
        }),
      },     
      {
        field: 'last_box',
        headerName: 'Caja Anterior',
        align: 'right',
        minWidth: 80,
        maxWidth: 100,
        renderCell: createCurrencyCell('last_box'),
      },
      {
        field: 'value_box',
        headerName: 'Total Caja',
        align: 'right',
        minWidth: 80,
        maxWidth: 100,
        renderCell: createCurrencyCell('value_box'),
      },
    ];

    return (
      <SimpleCustomTable
        rows={rows}
        columns={columns}
        maxHeight={600}
        showBorders
        showCount
        exportCsv
        exportFileName="consolidado-dia.csv"
      />
    );
  };

  return (
    <HeaderResponsive>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Title>Resumen Consolidado Día</Title>
        <div style={{ marginLeft: "auto" }}>

          <Button variant="contained" onClick={submit} startIcon={!isMobile && <SearchIcon />}>
            {isMobile ? <SearchIcon /> : 'Buscar'}
          </Button>
        </div>

      </Box>

      <Card>
        <CardContent>
          <CollectFilter
            start_date={start_date}
            end_date={end_date}
            office_id={office_id}
            setStart_date={setStart_date}
            setEnd_date={setEnd_date}
            setOffice_id={setOffice_id}
            offices={offices}
            collectsOffice={collectsOffice}
            selectedCollects={selectedCollects}
            setSelectedCollects={setSelectedCollects}
            selectAll={selectAll}
            setSelectAll={setSelectAll}
            handleCheckboxChange={handleCheckboxChange}
            handleSelectAll={handleSelectAll}
          />
          <Divider sx={{ my: 4 }} />
          <PageScrollArea sx={{ maxHeight: 800 }}>
            {datas.length > 0 ? (
              <Box sx={{ maxHeight: 600, overflow: 'scroll' }}>
                <CardContent sx={{ width: (t) => stickyListRegionWidth(t, drawerOpen), maxWidth: '100%', boxSizing: 'border-box', overflowX: 'auto', }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Reporte Resumen Consolidado Día
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Fechas:</strong> {start_date} al {end_date}
                      </Typography>
                    </Grid>
                  </Grid>
                  {renderDataGridResumen(datas)}
                </CardContent>
              </Box>
            ) : (
              <EmptyStateMessage message="No existen registros" align="left" />
            )}
            <Divider sx={{ my: 4 }} />
          </PageScrollArea>
          <ModalOfficeExpense
            open={openModal}
            onClose={handleCloseModal}
            clientData={modalData}
            showAlert={showAlert}
            showLoading={showLoading}
            hideLoading={hideLoading} />
          <ModalLendRefinance
            open={openModalLend}
            onClose={handleCloseModalLend}
            clientLend={modalLend}
            showAlert={showAlert}
            showLoading={showLoading}
            hideLoading={hideLoading} />
        </CardContent>
      </Card>
    </HeaderResponsive >
  );
};

export default ResumenConsolidadoDia;
